/**
 * Locale-aware path helpers.
 *
 * URL strategy:
 *   • Ukrainian (default) is served at root paths: /, /prices, /gallery, /team
 *   • English is served under the /en prefix: /en, /en/prices, /en/gallery, /en/team
 *
 * This gives each language its own canonical, indexable URL (better SEO than
 * client-side language switching at the same URL).
 *
 * Hash links (e.g. "/#about") are also prefixed when language === 'en'
 * so the home anchor resolves to /en#about.
 */

export const SUPPORTED_LANGUAGES = ['uk', 'en'];

/**
 * Strip a leading "/en" segment from a pathname.
 * "/en/prices" → "/prices",   "/en" → "/",   "/prices" → "/prices"
 */
export const stripLangPrefix = (pathname) => {
    if (pathname === '/en') return '/';
    if (pathname.startsWith('/en/')) return pathname.slice(3); // remove "/en"
    return pathname;
};

/**
 * Detect language from a pathname. "/en[...]" → "en", everything else → "uk".
 */
export const detectLangFromPath = (pathname) => {
    if (pathname === '/en' || pathname.startsWith('/en/')) return 'en';
    return 'uk';
};

/**
 * Build a localized path for a given base (UA) path.
 *
 * localizePath('/prices', 'uk')   → '/prices'
 * localizePath('/prices', 'en')   → '/en/prices'
 * localizePath('/',       'en')   → '/en'
 * localizePath('/#about', 'en')   → '/en#about'
 */
export const localizePath = (basePath, language) => {
    if (language !== 'en') return basePath;
    if (basePath === '/') return '/en';
    if (basePath.startsWith('/#')) return `/en${basePath.slice(1)}`; // "/#about" → "/en#about"
    if (basePath.startsWith('/')) return `/en${basePath}`;
    return basePath;
};

/**
 * Build the canonical UA path for any pathname (strip /en prefix and any hash).
 * Used by SEOHead to compute hreflang alternates.
 */
export const toBasePath = (pathname) => stripLangPrefix(pathname);

